import BarcodeScannerBoxGun from "../../components/BarcodeScannerBoxGun";
import { Suspense, lazy, useCallback, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getDashboardContextValue } from "../../utils/dashboardContext";
import { printTicketPdf, verifyTicket } from "../../services/ticket";
import { useAuth } from "../../store/useAuth";
import { Barcode, Camera, Keyboard, RotateCcw, ScanLine, X } from "lucide-react";
import { getTicketPrintErrorMessage } from "../../utils/ticketPrintErrors";

const QRScannerPopup = lazy(() => import("../../components/scanQR/QrScannerPopup"));

const ScanBarcodeGun = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");
  const mode = searchParams.get("mode");

  let eventId = getDashboardContextValue("eventId");
  eventId = JSON.parse(eventId || "null");

  const userDetails = useAuth.getState().user;

  const [verificationResult, setVerificationResult] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [scannerResetToken, setScannerResetToken] = useState(0);
  const [lastScanMethod, setLastScanMethod] = useState(mode === "camera" ? "camera" : "gun");

  // Print state
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintAndCheckInLoading, setIsPrintAndCheckInLoading] = useState(false);
  const [isCertPrinting, setIsCertPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const [printSuccess, setPrintSuccess] = useState("");
  const printImgRef = useRef(null); // <-- proper React ref object

  // Handle scanner verification result
  const handleVerificationResult = (result) => {
    setVerificationResult(null);
    setIsVerifying(false);
    setPrintError("");
    setPrintSuccess("");
    setTimeout(() => setVerificationResult(result), 50);
  };

  const handleNewScan = () => {
    setVerificationResult(null);
    setIsVerifying(false);
    setCameraOpen(false);
    if (lastScanMethod === "camera") {
      setTimeout(() => setCameraOpen(true), 0);
    } else {
      setScannerResetToken((value) => value + 1);
    }
  };

  const handleCloseResultPopup = () => {
    setVerificationResult(null);
    setIsVerifying(false);
    if (lastScanMethod === "gun") {
      setScannerResetToken((value) => value + 1);
    }
  };

  const openPdfInNewTab = useCallback((pdfUrl, { tryPrint = false } = {}) => {
    const win = window.open(pdfUrl, "_blank");
    if (!win) {
      setPrintError("Chrome blocked the PDF tab. Please allow pop-ups and try again.");
      return null;
    }

    if (tryPrint) {
      const printOpenedPdf = () => {
        try {
          win.focus();
          win.print();
        } catch (error) { }
      };
      win.onload = printOpenedPdf;
      setTimeout(printOpenedPdf, 800);
    }

    return win;
  }, []);

  const openPrintPdfBuffer = useCallback((buffer, submissionId) => {
    const blob = new Blob([buffer], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    let opened = false;

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `ticket_${submissionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      opened = true;
    } else {
      opened = Boolean(openPdfInNewTab(blobUrl, { tryPrint: true }));
    }

    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    return opened;
  }, [openPdfInNewTab]);

  const verifyScannedCode = async (code) => {
    const codeNow = String(code || "").trim();
    if (!codeNow) return;
    setLastScanMethod("camera");
    setCameraOpen(false);
    setVerificationResult(null);
    setIsVerifying(true);
    try {
      const res = await verifyTicket({ submission_id: codeNow, event_id: eventId, type: type || "check_in" }, userDetails?.token);

      if (res.data.success && res.data.data) {
        const ticketData = res.data.data;
        handleVerificationResult({
          status: "success",
          message: res.data.message,
          first_name: ticketData.first_name,
          last_name: ticketData.last_name,
          profile_url: ticketData.profile_url,
          companyName: ticketData.companyName,
          scanned: ticketData.print_count,
          designation: ticketData.designation,
          event_name: ticketData.event_name,
          ticketClassName: ticketData.ticketClassName,
          ticket_class_name: ticketData.ticket_class_name,
          email: ticketData.email,
          type: type || "check_in",
          submission_id: ticketData.submission_id,
          print_count: ticketData.print_count,
          day_price_quantity: ticketData.day_price_quantity,
          all_days_print_check_in: ticketData.all_days_print_check_in,
        });
      } else {
        handleVerificationResult({
          status: "error",
          message: res.data.message || "Verification failed",
        });
      }
    } catch (error) {
      handleVerificationResult({
        status: "error",
        message: error?.response?.data?.message || "Invalid ticket",
      });
    } finally {
      setIsVerifying(false);
      setCameraOpen(false);
    }
  };

  const getTicketClassLabel = (result) =>
    result?.ticketClassName ||
    result?.ticket_class_name ||
    result?.ticketClass?.name ||
    result?.ticket_class?.name ||
    "Ticket";

  // Build QR URL for the submission_id
  const qrUrl = verificationResult?.submission_id
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      verificationResult.submission_id,
    )}`
    : "";

  // const handlePrint = () => {
  // if (!verificationResult?.submission_id) return;
  // setIsPrinting(true);
  //
  // const img = printImgRef.current;
  // const doPrint = () => {
  //   window.print();
  //   setIsPrinting(false);
  // };
  //
  // if (img && img.complete) {
  //   setTimeout(doPrint, 50);
  // } else if (img) {
  //   const onLoad = () => {
  //     img.removeEventListener("load", onLoad);
  //     setTimeout(doPrint, 50);
  //   };
  //   img.addEventListener("load", onLoad);
  // } else {
  //   setTimeout(doPrint, 100);
  // }
  // };

  const handlePrint = async ({ checkIn = false } = {}) => {
    if (!verificationResult?.submission_id) return false;

    setIsPrinting(true);
    setPrintError("");

    try {
      const isCertificate = type === "print-certificate";
      const scanMode = lastScanMethod === "gun" ? "machine" : "camera";
      const res = await printTicketPdf(verificationResult.submission_id, {
        printTemplate: isCertificate ? "forCertificatePrint" : "print",
        checkIn,
        method: scanMode === "machine" ? 0 : 1,
      });
      if (res.status === 200) {
        const messageHeader = res.headers?.["x-print-check-in-message"];
        const checkInMessage = checkIn && messageHeader ? decodeURIComponent(messageHeader) : "";
        if (checkIn && messageHeader) {
          setPrintSuccess(checkInMessage);
        } else {
          setPrintSuccess(isCertificate ? "Certificate sent to print." : "Badge sent to print.");
        }
        const opened = openPrintPdfBuffer(res.data, verificationResult.submission_id);
        return opened ? { printed: true, message: checkInMessage } : false;
      }

      setPrintError(getTicketPrintErrorMessage({ response: res }, "Failed to print ticket"));
      return false;
    } catch (error) {
      setPrintError(getTicketPrintErrorMessage(error, "Failed to print ticket"));
      return false;
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintBadgeAndCheckIn = async () => {
    if (!verificationResult?.submission_id || !eventId) {
      setPrintError("Missing ticket details for check-in.");
      return;
    }

    setIsPrintAndCheckInLoading(true);
    setPrintError("");
    setPrintSuccess("");

    try {
      const result = await handlePrint({ checkIn: true });
      if (!result?.printed) return;
      setPrintSuccess(result.message || `${type === "print-certificate" ? "Certificate" : "Ticket"} printed and checked in successfully.`);
    } catch (error) {
      setPrintError(
        getTicketPrintErrorMessage(error, `Failed to print ${type === "print-certificate" ? "certificate" : "ticket"} and check in.`),
      );
    } finally {
      setIsPrintAndCheckInLoading(false);
    }
  };

  const handlePrintCertificate = async () => {
    if (!verificationResult?.submission_id) return;

    setIsCertPrinting(true);
    setPrintError("");
    setPrintSuccess("");

    try {
      const scanMode = lastScanMethod === "gun" ? "machine" : "camera";
      const res = await printTicketPdf(verificationResult.submission_id, {
        printTemplate: "forCertificatePrint",
        checkIn: false,
        method: scanMode === "machine" ? 0 : 1,
      });
      if (res.status === 200) {
        setPrintSuccess("Certificate sent to print.");
        openPrintPdfBuffer(res.data, verificationResult.submission_id);
      } else {
        setPrintError(getTicketPrintErrorMessage({ response: res }, "Failed to print certificate"));
      }
    } catch (error) {
      setPrintError(getTicketPrintErrorMessage(error, "Failed to print certificate"));
    } finally {
      setIsCertPrinting(false);
    }
  };

  return (
    <div className="w-full flex h-full flex-col py-4 md:py-0 relative">
      {cameraOpen && !isVerifying && (
        <Suspense fallback={null}>
          <QRScannerPopup
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onStop={() => setCameraOpen(false)}
            onScan={({ data }) => verifyScannedCode(data)}
          />
        </Suspense>
      )}
      {/* PRINT STYLES */}
      <style>{`
        /* -------- GLOBAL PRINT BEHAVIOR -------- */
        @media print {
          html, body {
            padding: 0;
            margin: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page {
            /* EXACT BADGE SIZE: 5in wide x 3.5in tall */
            size: 5in 3.5in;
            margin: 0;
          }
          .app-root { display: none !important; }
          .print-root { display: block !important; }
        }
        /* On screen, hide the print area */
        .print-root { display: none; }

        /* -------- PRINT LAYOUT (only shown during print) -------- */
        .label-wrap {
          width: 5in;             /* card width */
          height: 3.5in;          /* card height */
          position: relative;
          overflow: hidden;
        }

        /* Absolute-positioned content layer */
        .label-stage {
          position: absolute;
          inset: 0;               /* fill full canvas */
        }

        /* --- Tune these offsets as needed for your pre-printed card --- */
        :root {
          /* QR size and position (bottom-right) */
          --qr-size: 0.95in;              /* QR square size */
          --qr-bottom-offset: 0.35in;     /* distance from bottom edge */
          --qr-right-offset: 0.45in;      /* distance from right edge */

          /* Name text block (bottom-left/center) */
          --name-bottom-offset: 0.35in;   /* distance from bottom edge */
          --name-left-offset: 0.45in;     /* left inset */
          --name-right-offset: 2.0in;     /* right inset to avoid QR area */
          --name-font-size: 18pt;         /* base font size */
        }

        .label-qr {
          position: absolute;
          width: var(--qr-size);
          height: var(--qr-size);
          bottom: var(--qr-bottom-offset);
          right: var(--qr-right-offset);
          object-fit: contain;
        }

        .label-name {
          position: absolute;
          bottom: var(--name-bottom-offset);
          left: var(--name-left-offset);
          right: var(--name-right-offset);
          font-family: Arial, Helvetica, sans-serif;
          font-weight: 700;
          font-size: var(--name-font-size);
          line-height: 1.1;
          text-align: left;         /* change to center if you prefer */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;  /* prevents clipping issues */
        }
      `}</style>
      {/* Main App UI (hidden on print) */}
      <div className="app-root w-full flex h-full flex-col px-3 py-2 md:px-8 md:py-6 lg:px-12">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 md:gap-6">
          <div className="flex items-center justify-between gap-4 flex-wrap pb-2 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:border-gray-400 shadow-sm transition-colors active:scale-95"
                title="Go back"
              >
                <span className="sr-only">Back</span>
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
                  {type === "print-certificate" ? "Certificate Print" : "Print Ticket"}
                </h1>
                <p className="text-sm md:text-base text-gray-500">
                  {type === "print-certificate"
                    ? "Scan ticket with barcode gun or camera to verify and print attendee certificate"
                    : "Scan ticket with barcode gun or camera to verify and print attendee ticket"}
                </p>
              </div>
            </div>
            {mode && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  {mode === "machine" ? "Machine Mode (Barcode Gun)" : "Camera Mode (QR Scanner)"}
                </span>
              </div>
            )}
          </div>

          <div className="grid w-full grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-5">
            <section className={`rounded-2xl border ${mode === "machine" ? "border-blue-500 ring-2 ring-blue-500/20 shadow-md" : "border-slate-200"} bg-white shadow-sm overflow-hidden`}>
              <div className="border-b border-slate-100 px-3 py-2.5 md:px-5 md:py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1E374D] text-white md:h-11 md:w-11">
                    <Barcode size={22} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900 md:text-lg">
                      Scan with Machine
                    </h2>
                    <p className="text-sm leading-5 text-slate-500">
                      Use the connected barcode scanner gun.
                    </p>
                  </div>
                  {mode === "machine" && (
                    <span className="ml-auto text-xs font-semibold bg-blue-600 text-white px-2.5 py-1 rounded-full">
                      Active Mode
                    </span>
                  )}
                </div>
              </div>
              <div className="flex min-h-[350px] flex-col items-center justify-center px-3 py-3 md:min-h-[420px] md:px-6 md:py-8">
                <BarcodeScannerBoxGun
                  eventId={eventId}
                  type={type || undefined}
                  setResult={handleVerificationResult}
                  token={userDetails?.token}
                  resetToken={scannerResetToken}
                  onScanStart={() => {
                    setLastScanMethod("gun");
                    setCameraOpen(false);
                    setVerificationResult(null);
                  }}
                  onLoadingChange={(loading) => {
                    setIsVerifying(loading);
                    if (loading) setVerificationResult(null);
                  }}
                />
                <div className="mt-2 w-full max-w-md sm:mt-5">
                  <div className="flex min-w-0 items-center justify-center gap-2 rounded-full bg-slate-50 px-4 py-2 text-center text-sm font-medium leading-5 text-slate-600">
                    <Keyboard size={16} className="shrink-0" />
                    <span>Press gun button to scan the ticket barcode.</span>
                  </div>
                </div>
              </div>
            </section>

            <section className={`rounded-2xl border ${mode && mode !== "machine" ? "border-blue-500 ring-2 ring-blue-500/20 shadow-md" : "border-slate-200"} bg-white shadow-sm overflow-hidden`}>
              <div className="border-b border-slate-100 px-3 py-2.5 md:px-5 md:py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white md:h-11 md:w-11">
                    <Camera size={22} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900 md:text-lg">
                      Scan with Camera
                    </h2>
                    <p className="text-sm leading-5 text-slate-500">
                      Open the device camera and scan a QR code.
                    </p>
                  </div>
                  {mode && mode !== "machine" && (
                    <span className="ml-auto text-xs font-semibold bg-blue-600 text-white px-2.5 py-1 rounded-full">
                      Active Mode
                    </span>
                  )}
                </div>
              </div>
              <div className="flex min-h-[200px] flex-col items-center justify-center px-3 py-3 text-center md:min-h-[420px] md:px-6 md:py-8">
                <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-blue-600 md:h-44 md:w-44 md:rounded-[2rem]">
                  <ScanLine size={48} strokeWidth={1.7} className="md:h-[86px] md:w-[86px]" />
                  <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm md:right-5 md:top-5 md:h-10 md:w-10">
                    <Camera size={18} />
                  </div>
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900 md:mt-7 md:text-xl">
                  Camera QR Scanner
                </h3>
                <p className="mt-1 max-w-sm text-sm leading-5 text-slate-500 md:mt-2 md:leading-6">
                  Best for laptop, tablet, or mobile camera scanning when the
                  barcode machine is not connected.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setLastScanMethod("camera");
                    setCameraOpen(true);
                  }}
                  disabled={isVerifying}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1E374D] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#172c3e] focus:outline-none focus:ring-2 focus:ring-[#1E374D] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 md:mt-7"
                >
                  <Camera size={18} />
                  Open Camera Scanner
                </button>
              </div>
            </section>
          </div>

        </div>

        {(isVerifying || verificationResult) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="relative w-full max-w-sm rounded-2xl bg-white p-4 shadow-2xl md:max-w-md md:p-6">
              {verificationResult && (
                <button
                  type="button"
                  onClick={handleCloseResultPopup}
                  aria-label="Close scan result"
                  className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E374D] focus:ring-offset-2"
                >
                  <X size={20} />
                </button>
              )}
              {isVerifying ? (
                <div className="flex min-h-[220px] flex-col items-center justify-center gap-3">
                  <div className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-gray-800 animate-spin" />
                  <div className="text-lg font-semibold text-gray-800">
                    Verifying ticket...
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  {verificationResult?.status === "error" ? (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-3xl font-bold text-red-500 md:h-24 md:w-24">
                      !
                    </div>
                  ) : verificationResult?.profile_url ? (
                    <img
                      src={
                        verificationResult?.profile_url ||
                        "https://i.ibb.co/Mk3V8z9J/scan-p.png"
                      }
                      alt="Attendee profile"
                      className="h-28 w-28 rounded-full object-cover md:h-36 md:w-36"
                    />
                  ) : (
                    <svg
                      width="112"
                      height="112"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-28 w-28 md:h-36 md:w-36"
                    >
                      <rect width="24" height="24" rx="12" fill="#E5E7EB" />
                      <path
                        fill="#9CA3AF"
                        d="M12 12c1.933 0 3.5-1.567 3.5-3.5S13.933 5 12 5s-3.5 1.567-3.5 3.5S10.067 12 12 12zm0 1.5c-2.33 0-7 1.167-7 3.5V19h14v-2c0-2.333-4.67-3.5-7-3.5z"
                      />
                    </svg>
                  )}

                  <h2 className="mt-3 text-center text-xl font-bold text-slate-900 md:text-2xl">
                    {verificationResult?.status === "error"
                      ? "Scan Failed"
                      : `${verificationResult?.first_name || ""} ${verificationResult?.last_name || ""}`.trim()}
                  </h2>
                  {verificationResult?.status === "error" ? (
                    <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-center text-sm font-semibold text-red-600">
                      {verificationResult?.message || "Invalid ticket"}
                    </div>
                  ) : (
                    <>
                      <h3 className="text-center text-base text-slate-600">
                        {verificationResult?.companyName}
                      </h3>
                      <h4 className="text-center text-base font-semibold text-slate-800">
                        {getTicketClassLabel(verificationResult)}
                      </h4>
                      <h5 className="text-center text-base font-semibold text-green-600">
                        Print - {verificationResult?.print_count ?? verificationResult?.scanned ?? 0}
                      </h5>
                      {printError && (
                        <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-center text-sm font-semibold text-red-600">
                          {printError}
                        </div>
                      )}
                      {printSuccess && (
                        <div className="mt-2 rounded-md bg-green-50 px-3 py-2 text-center text-sm font-semibold text-green-700">
                          {printSuccess}
                        </div>
                      )}
                    </>
                  )}

                  <div className="mt-4 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                    {verificationResult?.status !== "error" && (
                        <>
                          <button
                            onClick={handlePrint}
                            className="rounded-md bg-gray-800 px-4 py-2 font-semibold text-white disabled:opacity-60"
                            disabled={isPrinting || isPrintAndCheckInLoading}
                          >
                            {isPrinting ? "Preparing..." : (type === "print-certificate" ? "Print Certificate" : "Print Ticket")}
                          </button>
                          <button
                            onClick={handlePrintBadgeAndCheckIn}
                            className="rounded-md bg-green-700 px-4 py-2 font-semibold text-white disabled:opacity-60"
                            disabled={isPrinting || isPrintAndCheckInLoading}
                          >
                            {isPrintAndCheckInLoading
                              ? "Processing..."
                              : (type === "print-certificate" ? "Print Certificate & Check In" : "Print Ticket & Check In")}
                          </button>
                          <button
                            onClick={handlePrintCertificate}
                            className="rounded-md bg-indigo-700 px-4 py-2 font-semibold text-white disabled:opacity-60"
                            disabled={isPrinting || isPrintAndCheckInLoading || isCertPrinting}
                          >
                            {isCertPrinting ? "Preparing..." : "Print Certificate"}
                          </button>
                        </>
                      )}
                    <button
                      onClick={handleNewScan}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-gray-700 px-4 py-2 font-semibold text-white"
                    >
                      <RotateCcw size={16} />
                      New Scan
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* PRINT-ONLY DOM (shown only during window.print) */}
      <div className="print-root">
        <div className="label-wrap">
          <div className="label-stage">
            {/* QR Image (bottom-right) */}
            {qrUrl ? (
              <img
                ref={printImgRef}
                src={qrUrl}
                alt="QR"
                className="label-qr"
              />
            ) : null}

            {/* Full Name (bottom-left/center region) */}
            <div className="label-name">
              {(verificationResult?.first_name || "") +
                " " +
                (verificationResult?.last_name || "")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanBarcodeGun;
